import { useEffect, useState } from "react";
import axios from "axios";
import { useAdminAuth } from "@/lib/adminAuth";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { image_url: "", post_url: "", caption: "", sort_order: 0 };

export default function AdminInstagram() {
  const { authHeader } = useAdminAuth();
  const headers = { headers: authHeader() };
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/instagram`, headers);
      setPosts(r.data || []);
    } catch {
      toast.error("Couldn't load Instagram posts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: k === "sort_order" ? Number(e.target.value || 0) : e.target.value }));

  const onSave = async (e) => {
    e.preventDefault();
    if (!form.image_url || !form.post_url) {
      toast.error("Image URL and Post URL are required.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/instagram/${editingId}`, form, headers);
        toast.success("Updated.");
      } else {
        await axios.post(`${API}/admin/instagram`, form, headers);
        toast.success("Added.");
      }
      setForm(empty);
      setEditingId(null);
      load();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (p) => {
    setEditingId(p.id);
    setForm({ image_url: p.image_url, post_url: p.post_url, caption: p.caption || "", sort_order: p.sort_order || 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this post from the feed?")) return;
    try {
      await axios.delete(`${API}/admin/instagram/${id}`, headers);
      toast.success("Removed.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-instagram-tab">
      <div className="border border-charcoal/10 bg-ivory p-6 md:p-8 mb-8">
        <div className="overline">{editingId ? "Edit Post" : "Add a Featured Post"}</div>
        <h3 className="font-serif-heading text-2xl text-charcoal mt-2 mb-1">
          Curate <span className="italic text-bronze-deep">@primogroupottawa</span>
        </h3>
        <p className="text-xs text-mute mb-6">
          Add up to 3 posts (the public site shows the top 3, sorted by Order
          ascending then newest). Tip: open the post on Instagram, right-click the
          image → &ldquo;Copy Image Address&rdquo;, then paste here.
        </p>
        <form onSubmit={onSave} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          <div className="md:col-span-2">
            <label className="overline block mb-2">Image URL *</label>
            <input
              className="line-input"
              value={form.image_url}
              onChange={set("image_url")}
              placeholder="https://..."
              required
              data-testid="ig-form-image"
            />
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-2">Post URL *</label>
            <input
              className="line-input"
              value={form.post_url}
              onChange={set("post_url")}
              placeholder="https://www.instagram.com/p/..."
              required
              data-testid="ig-form-post"
            />
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-2">Caption (optional)</label>
            <textarea
              rows={2}
              className="line-input resize-none"
              value={form.caption}
              onChange={set("caption")}
              data-testid="ig-form-caption"
            />
          </div>
          <div>
            <label className="overline block mb-2">Order</label>
            <input
              type="number"
              className="line-input"
              value={form.sort_order}
              onChange={set("sort_order")}
              data-testid="ig-form-order"
            />
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-3 mt-2">
            <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid="ig-form-submit">
              {saving ? (<><Loader2 size={14} className="animate-spin mr-2" /> Saving…</>) : editingId ? (<><Save size={14} className="mr-2" /> Update Post</>) : (<><Plus size={14} className="mr-2" /> Add Post</>)}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => { setEditingId(null); setForm(empty); }}
                className="text-xs uppercase tracking-[0.22em] text-charcoal/70 hover:text-charcoal underline-offset-2 hover:underline"
                data-testid="ig-form-cancel"
              >
                Cancel edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-12 text-center text-mute text-sm flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : posts.length === 0 ? (
        <div className="border border-dashed border-charcoal/20 bg-cream/40 py-16 text-center">
          <h3 className="font-serif-heading text-2xl text-charcoal">No featured posts yet.</h3>
          <p className="text-mute text-sm mt-2 max-w-sm mx-auto">
            The Instagram strip on the landing page stays hidden until you add at least one post.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ig-admin-list">
          {posts.map((p) => (
            <article key={p.id} className="border border-charcoal/10 bg-ivory" data-testid={`ig-admin-${p.id}`}>
              <div className="aspect-square bg-charcoal/5 overflow-hidden">
                {/* image of the featured instagram post */}
                <img src={p.image_url} alt={p.caption || "Featured instagram"} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-5">
                <div className="overline mb-2">Order · {p.sort_order ?? 0}</div>
                {p.caption && <p className="text-sm text-ink line-clamp-3 mb-3">{p.caption}</p>}
                <div className="flex flex-wrap gap-3 mt-2">
                  <button onClick={() => onEdit(p)} className="text-[11px] uppercase tracking-[0.22em] border border-charcoal/30 px-3 py-2 hover:bg-charcoal hover:text-ivory transition-colors" data-testid={`ig-edit-${p.id}`}>
                    Edit
                  </button>
                  <a href={p.post_url} target="_blank" rel="noopener noreferrer" className="text-[11px] uppercase tracking-[0.22em] border-b border-charcoal pb-1 hover:text-bronze hover:border-bronze">
                    Open on IG
                  </a>
                  <button onClick={() => onDelete(p.id)} className="ml-auto text-mute hover:text-red-700" data-testid={`ig-delete-${p.id}`} aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
